<template>
  <NavBar pageName="Inventory Statuses"></NavBar>
  <v-container style="margin-top: 185px;">
    <v-btn variant="text" size="small" @click="$router.push('/settings/inventory')" class="mb-2">
      <v-icon size="small" class="mr-1">mdi-arrow-left</v-icon>
      Inventory Settings
    </v-btn>
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>Inventory Statuses</span>
        <v-btn color="success" size="small" @click="openAddDialog">
          <v-icon class="mr-1">mdi-plus</v-icon>
          Add
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="statuses"
          :loading="loading"
          :items-per-page="20"
          class="elevation-1"
        >
          <template v-slot:[`item.created_at`]="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-btn
              icon
              size="small"
              variant="text"
              color="primary"
              @click="editStatus(item)"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              icon
              size="small"
              variant="text"
              color="error"
              @click="confirmDelete(item)"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Add/Edit Dialog -->
    <v-dialog v-model="showDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title>
          {{ editingId ? 'Edit' : 'Add' }} Inventory Status
        </v-card-title>

        <v-card-text>
          <v-text-field
            v-model="formData.name"
            label="Name"
            variant="outlined"
            density="compact"
            class="mb-3"
            :rules="[v => !!v || 'Name is required']"
          ></v-text-field>

          <v-textarea
            v-model="formData.description"
            label="Description"
            variant="outlined"
            density="compact"
            rows="3"
          ></v-textarea>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            @click="saveStatus"
            :loading="saving"
            :disabled="!formData.name"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title>Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete this status? This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" @click="deleteStatus" :loading="deleting">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="3000">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import NavBar from "@/components/NavBar.vue";
import store from "@/store";
import { log } from "@/composables/useLog";
import { getInventoryStatuses, saveInventoryStatus, deleteInventoryStatus as deleteInventoryStatusAPI } from "@/composables/useGoodsInventoryAPI";

export default defineComponent({
  name: 'InventoryStatusesList',
  components: {
    NavBar
  },
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      statuses: [],
      showDialog: false,
      showDeleteDialog: false,
      editingId: null,
      deleteId: null,
      formData: {
        name: '',
        description: ''
      },
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success'
    }
  },
  computed: {
    token() {
      return store.getters.getToken;
    },
    headers() {
      return [
        { title: 'ID', key: 'id', width: '80px' },
        { title: 'Name', key: 'name' },
        { title: 'Description', key: 'description' },
        { title: 'Created At', key: 'created_at', width: '180px' },
        { title: 'Actions', key: 'actions', sortable: false, width: '120px' }
      ];
    }
  },
  methods: {
    async loadStatuses() {
      this.loading = true;
      try {
        const response = await getInventoryStatuses(this.token);
        if (!response.error) {
          this.statuses = response.inventory_statuses || [];
        } else {
          this.showMessage(response.message || 'Error loading statuses', 'error');
        }
      } catch (err) {
        log("Error loading statuses:", err);
        this.showMessage('Error loading statuses', 'error');
      } finally {
        this.loading = false;
      }
    },

    openAddDialog() {
      this.editingId = null;
      this.formData = { name: '', description: '' };
      this.showDialog = true;
    },

    editStatus(item) {
      this.editingId = item.id;
      this.formData = { name: item.name, description: item.description || '' };
      this.showDialog = true;
    },

    closeDialog() {
      this.showDialog = false;
      this.editingId = null;
      this.formData = { name: '', description: '' };
    },

    async saveStatus() {
      this.saving = true;
      try {
        const data = {
          id: this.editingId,
          name: this.formData.name,
          description: this.formData.description
        };

        const response = await saveInventoryStatus(this.token, data);
        if (!response.error) {
          this.showMessage(response.message || 'Status saved', 'success');
          this.closeDialog();
          await this.loadStatuses();
        } else {
          this.showMessage(response.message || 'Error saving status', 'error');
        }
      } catch (err) {
        log("Error saving status:", err);
        this.showMessage('Error saving status', 'error');
      } finally {
        this.saving = false;
      }
    },

    confirmDelete(item) {
      this.deleteId = item.id;
      this.showDeleteDialog = true;
    },

    async deleteStatus() {
      this.deleting = true;
      try {
        const response = await deleteInventoryStatusAPI(this.token, this.deleteId);
        if (!response.error) {
          this.showMessage(response.message || 'Status deleted', 'success');
          this.showDeleteDialog = false;
          this.deleteId = null;
          await this.loadStatuses();
        } else {
          this.showMessage(response.message || 'Error deleting status', 'error');
        }
      } catch (err) {
        log("Error deleting status:", err);
        this.showMessage('Error deleting status', 'error');
      } finally {
        this.deleting = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },

    showMessage(message, color) {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.showSnackbar = true;
    }
  },
  mounted() {
    this.loadStatuses();
  }
});
</script>
